/**
 * Componente de barra de filtros para el reporte de logística
 * Filters bar component for logistics report
 */

import { createHttpClient } from '@adapters/HttpClient';
import { faSliders, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CouriersApiAdapter } from '@modules/Orders/List/adapters/CouriersApiAdapter';
import { PaymentMethodsApiAdapter } from '@modules/Orders/List/adapters/PaymentMethodsApiAdapter';
import { ShopsApiAdapter } from '@modules/Orders/List/adapters/ShopsApiAdapter';
import { Button } from '@shared/components/ui/button';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';
import { useIsMobile } from '@shared/hooks/useMobile';
import { apiClientWH } from '@shared/services';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

// Helper para formatear fecha sin problemas de zona horaria
// Helper to format date without timezone issues
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface LogisticsFiltersBarProps {
  startDate?: string;
  endDate?: string;
  dateColumn?: 'processed_at' | 'created_at' | 'updated_at' | 'sent_at';
  paymentMethodId?: number | null;
  shopId?: number | null;
  courierId?: number | null;
  onFiltersChange: (filters: {
    startDate?: string;
    endDate?: string;
    dateColumn?: 'processed_at' | 'created_at' | 'updated_at' | 'sent_at';
    paymentMethodId?: number | null;
    shopId?: number | null;
    courierId?: number | null;
  }) => void;
  getDefaultStartDate: () => string;
  getDefaultEndDate: () => string;
}

export const LogisticsFiltersBar = ({
  startDate,
  endDate,
  dateColumn = 'sent_at',
  paymentMethodId,
  shopId,
  courierId,
  onFiltersChange,
  getDefaultStartDate,
  getDefaultEndDate,
}: LogisticsFiltersBarProps) => {
  const { t } = useTranslation('logisticsModule');

  // Estados locales para los filtros
  // Local states for filters
  const [localStartDate, setLocalStartDate] = useState<string | undefined>(startDate);
  const [localEndDate, setLocalEndDate] = useState<string | undefined>(endDate);
  const [localDateColumn, setLocalDateColumn] = useState<'processed_at' | 'created_at' | 'updated_at' | 'sent_at'>(
    dateColumn,
  );
  const [localPaymentMethodId, setLocalPaymentMethodId] = useState<string>(
    paymentMethodId !== null && paymentMethodId !== undefined ? paymentMethodId.toString() : '',
  );
  const [localShopId, setLocalShopId] = useState<string>(
    shopId !== null && shopId !== undefined ? shopId.toString() : '',
  );
  const [localCourierId, setLocalCourierId] = useState<string>(
    courierId !== null && courierId !== undefined ? courierId.toString() : '',
  );

  // Sincronizar estados locales con props
  // Sync local states with props
  useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
    setLocalDateColumn(dateColumn);
    setLocalPaymentMethodId(
      paymentMethodId !== null && paymentMethodId !== undefined ? paymentMethodId.toString() : '',
    );
    setLocalShopId(shopId !== null && shopId !== undefined ? shopId.toString() : '');
    setLocalCourierId(courierId !== null && courierId !== undefined ? courierId.toString() : '');
  }, [startDate, endDate, dateColumn, paymentMethodId, shopId, courierId]);

  // Opciones para columna de fecha (solo 2 opciones)
  // Options for date column (only 2 options)
  const dateColumnOptions = [
    { label: t('filters.date_column.sent_at'), value: 'sent_at' },
    { label: t('filters.date_column.created_at'), value: 'created_at' },
  ];

  // Estados para opciones dinámicas
  // States for dynamic options
  const [shopOptions, setShopOptions] = useState<{ label: string; value: string }[]>([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<{ label: string; value: string }[]>([]);
  const [courierOptions, setCourierOptions] = useState<{ label: string; value: string }[]>([]);
  const [isLoadingShops, setIsLoadingShops] = useState(false);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [isLoadingCouriers, setIsLoadingCouriers] = useState(false);

  // Cargar opciones de tiendas
  // Load shop options
  const loadShops = useCallback(async () => {
    try {
      setIsLoadingShops(true);
      const httpClient = createHttpClient(apiClientWH);
      const shopsAdapter = new ShopsApiAdapter(httpClient);
      const shops = await shopsAdapter.getAll();
      setShopOptions([
        { label: t('filters.all', 'Todos'), value: '' },
        ...shops.map((shop) => ({
          label: shop.name,
          value: shop.id.toString(),
        })),
      ]);
    } catch (error) {
      console.error('Error loading shops:', error);
      setShopOptions([{ label: t('filters.all', 'Todos'), value: '' }]);
    } finally {
      setIsLoadingShops(false);
    }
  }, [t]);

  // Cargar opciones de métodos de pago
  // Load payment method options
  const loadPaymentMethods = useCallback(async () => {
    try {
      setIsLoadingPaymentMethods(true);
      const httpClient = createHttpClient(apiClientWH);
      const paymentMethodsAdapter = new PaymentMethodsApiAdapter(httpClient);
      const paymentMethods = await paymentMethodsAdapter.getAll();
      setPaymentMethodOptions([
        { label: t('filters.all', 'Todos'), value: '' },
        ...paymentMethods.map((method) => ({
          label: method.name,
          value: method.id.toString(),
        })),
      ]);
    } catch (error) {
      console.error('Error loading payment methods:', error);
      setPaymentMethodOptions([{ label: t('filters.all', 'Todos'), value: '' }]);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  }, [t]);

  // Cargar opciones de couriers
  // Load courier options
  const loadCouriers = useCallback(async () => {
    try {
      setIsLoadingCouriers(true);
      const httpClient = createHttpClient(apiClientWH);
      const couriersAdapter = new CouriersApiAdapter(httpClient);
      const couriers = await couriersAdapter.getAll();
      setCourierOptions([
        { label: t('filters.all', 'Todos'), value: '' },
        ...couriers.map((courier) => ({
          label: courier.name,
          value: courier.id.toString(),
        })),
      ]);
    } catch (error) {
      console.error('Error loading couriers:', error);
      setCourierOptions([{ label: t('filters.all', 'Todos'), value: '' }]);
    } finally {
      setIsLoadingCouriers(false);
    }
  }, [t]);

  // Cargar opciones al montar el componente
  // Load options when component mounts
  useEffect(() => {
    loadShops();
    loadPaymentMethods();
    loadCouriers();
  }, [loadShops, loadPaymentMethods, loadCouriers]);

  // Handler para cambio de rango de fechas desde DateRangePicker
  // Handler for date range change from DateRangePicker
  const handleDateRangeUpdate = ({ range }: { range: DateRange }) => {
    if (range.from && range.to) {
      // Usar formatDateLocal para evitar problemas de zona horaria
      // Use formatDateLocal to avoid timezone issues
      const newStartDate = formatDateLocal(range.from);
      const newEndDate = formatDateLocal(range.to);

      setLocalStartDate(newStartDate);
      setLocalEndDate(newEndDate);

      onFiltersChange({
        startDate: newStartDate,
        endDate: newEndDate,
        dateColumn: localDateColumn,
        paymentMethodId:
          localPaymentMethodId && localPaymentMethodId.trim() !== '' ? parseInt(localPaymentMethodId) : null,
        shopId: localShopId && localShopId.trim() !== '' ? parseInt(localShopId) : null,
        courierId: localCourierId && localCourierId.trim() !== '' ? parseInt(localCourierId) : null,
      });
    }
  };

  // Ref para evitar aplicar filtros en el montaje inicial
  // Ref to avoid applying filters on initial mount
  const isInitialMount = useRef(true);

  // Aplicar filtros automáticamente cuando cambian los valores (excepto en el montaje inicial)
  // Apply filters automatically when values change (except on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    onFiltersChange({
      startDate: localStartDate,
      endDate: localEndDate,
      dateColumn: localDateColumn,
      paymentMethodId:
        localPaymentMethodId && localPaymentMethodId.trim() !== '' ? parseInt(localPaymentMethodId) : null,
      shopId: localShopId && localShopId.trim() !== '' ? parseInt(localShopId) : null,
      courierId: localCourierId && localCourierId.trim() !== '' ? parseInt(localCourierId) : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localDateColumn, localPaymentMethodId, localShopId, localCourierId]);

  // Handler para limpiar todos los filtros
  // Handler to clear all filters
  const handleClearFilters = useCallback(() => {
    const defaultStartDate = getDefaultStartDate();
    const defaultEndDate = getDefaultEndDate();
    const defaultDateColumn: 'processed_at' | 'created_at' | 'updated_at' | 'sent_at' = 'sent_at';

    setLocalStartDate(defaultStartDate);
    setLocalEndDate(defaultEndDate);
    setLocalDateColumn(defaultDateColumn);
    setLocalPaymentMethodId('');
    setLocalShopId('');
    setLocalCourierId('');

    onFiltersChange({
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      dateColumn: defaultDateColumn,
      paymentMethodId: null,
      shopId: null,
      courierId: null,
    });
  }, [getDefaultStartDate, getDefaultEndDate, onFiltersChange]);

  // Detectar si estamos en mobile
  // Detect if we are on mobile
  const isMobile = useIsMobile();

  // Componente para renderizar el contenido de los filtros
  // Component to render filters content
  const renderFiltersContent = () => (
    <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3 flex-wrap">
      {/* Filtro de fecha - Date filter */}
      <div className="flex items-end gap-2 flex-1 min-w-[300px]">
        <div className="flex-1">
          <label className="block text-xs text-muted-foreground mb-1.5">{t('filters.start_date', 'Fecha')}</label>
          <DateRangePicker
            initialDateFrom={localStartDate}
            initialDateTo={localEndDate}
            onUpdate={handleDateRangeUpdate}
            align="start"
            showPresets={true}
            triggerClassName="bg-card-bg border-card-border w-full"
            placeholder={t('filters.select_date_range', 'Seleccionar rango')}
            dataTestId="date-range-picker"
          />
        </div>
        <div className="w-[180px] flex-shrink-0">
          <label className="block text-xs text-muted-foreground mb-1.5">
            {t('filters.date_column.label_short', 'Columna')}
          </label>
          <Combobox
            options={dateColumnOptions}
            value={localDateColumn}
            setValue={(value) => setLocalDateColumn(value as typeof localDateColumn)}
            placeholder={t('filters.date_column.label', 'Columna de fecha')}
            dataTestId="date-column-selector"
          />
        </div>
      </div>

      {/* Filtros adicionales - Additional filters */}
      <div className="flex items-end gap-2 flex-wrap">
        <div className="w-[180px]">
          <label className="block text-xs text-muted-foreground mb-1.5">
            {t('filters.payment_method', 'Método de pago')}
          </label>
          <Combobox
            options={paymentMethodOptions}
            value={localPaymentMethodId}
            setValue={setLocalPaymentMethodId}
            placeholder={t('filters.all', 'Todos')}
            dataTestId="payment-method-selector"
            disabled={isLoadingPaymentMethods}
          />
        </div>
        <div className="w-[180px]">
          <label className="block text-xs text-muted-foreground mb-1.5">{t('filters.shop', 'Tienda')}</label>
          <Combobox
            options={shopOptions}
            value={localShopId}
            setValue={setLocalShopId}
            placeholder={t('filters.all', 'Todos')}
            dataTestId="shop-selector"
            disabled={isLoadingShops}
          />
        </div>
        <div className="w-[180px]">
          <label className="block text-xs text-muted-foreground mb-1.5">{t('filters.courier', 'Courier')}</label>
          <Combobox
            options={courierOptions}
            value={localCourierId}
            setValue={setLocalCourierId}
            placeholder={t('filters.all', 'Todos')}
            dataTestId="courier-selector"
            disabled={isLoadingCouriers}
          />
        </div>
        <div className="flex items-end h-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground shrink-0"
            data-testid="clear-filters-button"
            title={t('filters.clear', 'Limpiar filtros')}
          >
            <FontAwesomeIcon icon={faXmark} className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );

  // Renderizado para mobile - Botón con popover
  // Rendering for mobile - Button with popover
  if (isMobile) {
    return (
      <div className="mb-4">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="flex items-center gap-2" data-testid="mobile-filters-button">
              <FontAwesomeIcon icon={faSliders} className="w-3.5 h-3.5" />
              {t('filters.title', 'Filtros')}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[calc(100vw-2rem)] max-w-md p-4 max-h-[calc(100vh-4rem)] overflow-y-auto"
            align="start"
          >
            <div className="space-y-4">{renderFiltersContent()}</div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  // Renderizado para desktop - Sin borde, todo en línea
  // Rendering for desktop - No border, all in line
  return <div className="mb-4">{renderFiltersContent()}</div>;
};
